import React, { useState, useEffect } from 'react';
import {
  string, shape as shapeType, func, number
} from 'prop-types';
import {
  arrayOf,
  params,
  shape,
  string as stringType,
  customType,
  useDataModel,
  extend,
} from '@thd-nucleus/data-sources';
import {
  Button,
  ButtonGroup,
  DrawerBody,
  DrawerFooter,
  Typography,
  Alert,
  Link,
  SkeletonBlock
} from '@one-thd/sui-atomic-components';

export const PriceChangeWarningDrawer = ({
  itemId,
  quantity,
  price,
  fulfillmentLocation,
  fulfillmentMethod,
  localStoreId,
  customerInfo,
  setQuoteName,
  setError,
  selectedQuote,
  onClose,
  setPriceChangeWarningDrawer,
  setSuccess,
  setErrorDrawer
}) => {
  const { svocId } = customerInfo;
  const [showLoader, setShowLoader] = useState(false);

  const [
    addToThisQuote,
    { data: addToQuoteData, loading: addToQuoteLoading, error: addToQuoteError }
  ] = useDataModel('addItemToQuote', {
    variables: {
      quoteId: selectedQuote?.quoteId,
      svocId,
      userId: selectedQuote?.userId,
      lineItems: [{
        itemId,
        quantity,
        fulfillmentLocation,
        fulfillmentMethod
      }],
      localStoreId
    },
    ssr: false
  });

  const addItemToQuote = () => {
    setShowLoader(true);
    addToThisQuote();
  };

  const triggerAnalytics = () => {
    const quoteId = addToQuoteData?.addItemToQuote?.quoteId;

    window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.quoteAdd', {
      quoteType: 'online',
      quoteId,
      item: {
        quoteLocation: 'pip',
        quantity,
        price: {
          basePrice: price
        },
        productInfo: {
          sku: itemId
        }
      }
    });
  };

  useEffect(() => {
    if (addToQuoteData && !addToQuoteLoading && !addToQuoteError) {
      setShowLoader(false);
      setQuoteName(selectedQuote.quoteName);
      setPriceChangeWarningDrawer(false);
      setSuccess(true);
      triggerAnalytics();
    } else if (addToQuoteError) {
      setShowLoader(false);
      setError(addToQuoteError);
      setErrorDrawer(true);
      setPriceChangeWarningDrawer(false);
    }
  }, [addToQuoteData, addToQuoteLoading, addToQuoteError]);

  if (showLoader) {
    return (
      <DrawerBody>
        <SkeletonBlock />
      </DrawerBody>
    );
  }

  return (
    <>
      <DrawerBody>
        <Alert status="warning">
          <Typography>Updated Quoted Pricing</Typography>
          Prices on your existing quote{' '}
          <u>{`${selectedQuote.quoteName}`}</u> will
          update when you add this item. Are you sure you want to proceed?
        </Alert>
      </DrawerBody>
      <DrawerFooter>
        <ButtonGroup orientation="vertical">
          <Button
            fullWidth
            variant="primary"
            onClick={addItemToQuote}
            data-testid="add-item-to-quote"
          >
            Add to Quote
          </Button>
          <Button
            fullWidth
            variant="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
        </ButtonGroup>
      </DrawerFooter>
    </>
  );
};

PriceChangeWarningDrawer.propTypes = {
  customerInfo: shapeType({
    svocId: string.isRequired,
    userId: string.isRequired,
  }).isRequired,
  itemId: string.isRequired,
  quantity: number.isRequired,
  price: number.isRequired,
  fulfillmentLocation: string.isRequired,
  fulfillmentMethod: string.isRequired,
  localStoreId: string.isRequired,
  setQuoteName: func.isRequired,
  setError: func.isRequired,
  selectedQuote: shapeType({
    quoteId: string.isRequired,
    quoteName: string.isRequired,
    userId: string.isRequired
  }).isRequired,
  onClose: func.isRequired,
  setPriceChangeWarningDrawer: func.isRequired,
  setSuccess: func.isRequired,
  setErrorDrawer: func.isRequired
};

PriceChangeWarningDrawer.displayName = 'PriceChangeWarningDrawer';

PriceChangeWarningDrawer.dataModel = extend(
  {
    addItemToQuote: params({
      quoteId: stringType().isRequired(),
      svocId: stringType().isRequired(),
      userId: stringType().isRequired(),
      lineItems: arrayOf(
        customType('LineItemInput!').shape({
          itemId: stringType(),
          quantity: stringType(),
          fulfillmentLocation: stringType(),
          fulfillmentMethod: stringType()
        })
      ).isRequired(),
      localStoreId: stringType().isRequired(),
    }).mutation().shape({
      quoteId: stringType(),
      errorData: arrayOf(
        shape({
          errors: arrayOf(
            shape({
              id: stringType(),
              description: stringType(),
              submessage: stringType(),
              errorCode: stringType(),
              errorProperties: shape({
                errorProperty: shape({
                  name: stringType(),
                  value: stringType()
                })
              })
            })
          ),
          httpStatus: stringType(),
          userId: stringType(),
          customerAccountId: stringType(),
          correlationId: stringType(),
          correlationType: stringType()
        })
      )
    })
  });